/**
 * View Registry
 *
 * Maps view IDs to their React components.
 * This is the source of truth for which component renders for each view.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import { lazy } from 'react';

// =============================================================================
// Eagerly Loaded Components (frequently used, fast loading)
// =============================================================================

import PAQueue from '../components/queues/PAQueue';
import AssemblyQueue from '../components/queues/AssemblyQueue';
import ProjectSchedule from '../components/ProjectSchedule';
import TeamView from '../components/TeamView';
import PADashboard from '../components/PADashboard';  // Printers view
import ProductionScheduleBoard from '../components/ProductionScheduleBoard';
import UnifiedProjectBoard from '../components/UnifiedProjectBoard';
import UnifiedProjectView from '../components/UnifiedProjectView';
import MyWorkView from '../components/views/MyWorkView';  // Self-contained wrapper
import TimeTrackerView from '../components/views/TimeTrackerView';  // Legacy time tracker
import TeamPrintersView from '../components/views/TeamPrintersView';  // Team & Printers combined
import TodaysSessions from '../components/TodaysSessions';

// PlaceholderView is imported eagerly since it's used for multiple placeholder views
import PlaceholderView from '../components/navigation/PlaceholderView';

// Project Detail page for work center views
const ProjectDetailPage = lazy(() => import('../components/project/ProjectDetailPage'));

// Pipeline views (new department pipelines)
const MasterPipeline = lazy(() => import('../components/pipelines/MasterPipeline'));
const PAPipeline = lazy(() => import('../components/pipelines/PAPipeline'));
const AssemblyPipeline = lazy(() => import('../components/pipelines/AssemblyPipeline'));

// Unified Project Page
const ProjectPage = lazy(() => import('../components/project/ProjectPage'));

// =============================================================================
// Lazily Loaded Components (heavy, less frequent)
// =============================================================================

const PMCommandCenter = lazy(() => import('../components/PMCommandCenter'));
const PMInterface = lazy(() => import('../components/pm/PMInterface'));
const PMWorkOrderGenerator = lazy(() => import('../components/PMWorkOrderGenerator'));
const SessionDashboard = lazy(() => import('../components/SessionDashboard'));
const ReportsDashboard = lazy(() => import('../components/ReportsDashboard'));
const AdminUserManagement = lazy(() => import('../components/AdminUserManagement'));
const AdminTickets = lazy(() => import('../components/AdminTickets'));
const DataQualityDashboard = lazy(() => import('../components/DataQualityDashboard'));
const FeedbackDashboard = lazy(() => import('../components/FeedbackDashboard'));
const ReworkDashboard = lazy(() => import('../components/admin/ReworkDashboard'));
const NotificationAdmin = lazy(() => import('../components/admin/NotificationAdmin'));
const SystemHealthDashboard = lazy(() => import('../components/admin/SystemHealthDashboard'));
const WorkInstructionsPage = lazy(() => import('../components/WorkInstructionsPage'));
const DesignSystem = lazy(() => import('../pages/DesignSystem'));
const NotificationsPage = lazy(() => import('../components/NotificationsPage'));
const TicketHistory = lazy(() => import('../components/reporting/TicketHistory'));

// Revenue Dashboard components (lazily loaded)
const RevenueDashboard = lazy(() => import('../components/dashboard/RevenueDashboard'));
const SalesReps = lazy(() => import('../components/dashboard/SalesReps'));
const PendingQuotesManagement = lazy(() => import('../components/dashboard/PendingQuotesManagement'));
const DataDefinitions = lazy(() => import('../components/dashboard/DataDefinitions'));
const HowToGuide = lazy(() => import('../components/dashboard/HowToGuide'));

// =============================================================================
// View Registry
// =============================================================================

export const VIEW_REGISTRY = {
  // ---------------------------------------------------------------------------
  // Production Section
  // ---------------------------------------------------------------------------
  'time-tracker': {
    component: TimeTrackerView,
    title: 'Track Time',
  },
  'team-printers': {
    component: TeamPrintersView,
    title: 'Team & Printers',
  },
  'my-work': {
    component: MyWorkView,
    title: 'My Work',
  },
  'my-notifications': {
    component: NotificationsPage,
    title: 'My Notifications',
    lazy: true,
  },
  'team-status': {
    component: TeamView,
    title: 'Team Status',
  },
  'printers': {
    component: PADashboard,
    title: 'Printers',
  },
  'unified-projects': {
    component: UnifiedProjectView,
    title: 'All Projects',
  },
  // Pipeline views (new department pipelines)
  'master-pipeline': {
    component: MasterPipeline,
    title: 'Master Pipeline',
    lazy: true,
  },
  'pa-pipeline': {
    component: PAPipeline,
    title: 'PA Pipeline',
    lazy: true,
  },
  'assembly-pipeline': {
    component: AssemblyPipeline,
    title: 'Assembly Pipeline',
    lazy: true,
  },
  'shipping-pipeline': {
    component: PlaceholderView,
    title: 'Shipping Pipeline',
    placeholder: true,
    placeholderDescription: 'Shipping pipeline - track shipments and deliveries.',
  },
  'billing-pipeline': {
    component: PlaceholderView,
    title: 'Billing Pipeline',
    placeholder: true,
    placeholderDescription: 'Billing pipeline - invoice and payment tracking.',
  },
  // Unified Project Page
  'project-page': {
    component: ProjectPage,
    title: 'Project',
    lazy: true,
  },
  // Work center views (legacy, now hidden)
  'pa-view': {
    component: PAQueue,
    title: 'PA View',
  },
  'assembly-view': {
    component: AssemblyQueue,
    title: 'Assembly View',
  },
  'paint-view': {
    component: PlaceholderView,
    title: 'Paint',
    placeholder: true,
    placeholderDescription: 'Paint queue - manage paint finishing work.',
  },
  'model-view': {
    component: PlaceholderView,
    title: 'Model',
    placeholder: true,
    placeholderDescription: 'Model queue - manage 3D model production.',
  },
  'shipping-view': {
    component: PlaceholderView,
    title: 'Shipping',
    placeholder: true,
    placeholderDescription: 'Shipping queue - coordinate packaging and shipments.',
  },
  // PM views
  'pm-center': {
    component: PMCommandCenter,
    title: 'PM Command Center (Legacy)',
    lazy: true,
  },
  'pm-interface': {
    component: PMInterface,
    title: 'PM Interface',
    lazy: true,
  },
  'wo-generator': {
    component: PMWorkOrderGenerator,
    title: 'Work Orders',
    lazy: true,
  },
  // Project Detail page - shared across work center views
  'project-detail': {
    component: ProjectDetailPage,
    title: 'Project Detail',
    lazy: true,
  },

  // ---------------------------------------------------------------------------
  // Scheduling Section
  // ---------------------------------------------------------------------------
  'project-tracker': {
    component: ProjectSchedule,
    title: 'Project Tracker',
  },
  'unified-board': {
    component: UnifiedProjectBoard,
    title: 'Unified Board',
  },
  'board': {
    component: ProductionScheduleBoard,
    title: 'Schedule Board',
  },
  'capacity': {
    component: PlaceholderView,
    title: 'Capacity',
    placeholder: true,
    placeholderDescription: 'View team workload and capacity planning across time horizons.',
  },
  'backlog': {
    component: PlaceholderView,
    title: 'Backlog',
    placeholder: true,
    placeholderDescription: 'Manage unscheduled work and prioritize what\'s next.',
  },
  'risks': {
    component: PlaceholderView,
    title: 'Risks',
    placeholder: true,
    placeholderDescription: 'Early warning system for delivery risks and blocked items.',
  },
  'changes': {
    component: PlaceholderView,
    title: 'Changes',
    placeholder: true,
    placeholderDescription: 'Audit trail for schedule-impacting decisions.',
  },

  // ---------------------------------------------------------------------------
  // Sales Section (Revenue Dashboard)
  // ---------------------------------------------------------------------------
  'revenue-dashboard': {
    component: RevenueDashboard,
    title: 'Revenue Dashboard',
    lazy: true,
  },
  'pending-quotes': {
    component: PendingQuotesManagement,
    title: 'Pending Quotes Management',
    lazy: true,
  },
  'sales-reps': {
    component: SalesReps,
    title: 'Sales Rep Pipeline',
    lazy: true,
  },
  'data-definitions': {
    component: DataDefinitions,
    title: 'Data Definitions',
    lazy: true,
  },
  'sales-help': {
    component: HowToGuide,
    title: 'How-To Guide',
    lazy: true,
  },

  // ---------------------------------------------------------------------------
  // Reporting Section
  // ---------------------------------------------------------------------------
  'time-ticket-data': {
    component: ReportsDashboard,
    title: 'Time Ticket Data',
    lazy: true,
    mode: 'reports',
  },
  // History is accessible via "View Session History" link but not in nav
  'history': {
    component: SessionDashboard,
    title: 'Session History',
    lazy: true,
    hidden: true, // Not in nav, only accessible via direct link
  },
  'ticket-history': {
    component: TicketHistory,
    title: 'Ticket History',
    lazy: true,
  },
  'more-reports': {
    component: PlaceholderView,
    title: 'More Reports',
    placeholder: true,
    placeholderDescription: 'Additional reporting features coming soon.',
  },
  // Legacy aliases for backwards compatibility
  'reports': {
    alias: 'time-ticket-data',
  },

  // ---------------------------------------------------------------------------
  // Administration Section
  // ---------------------------------------------------------------------------
  'users': {
    component: AdminUserManagement,
    title: 'User Management',
    lazy: true,
  },
  'tickets': {
    component: AdminTickets,
    title: 'Admin Tickets',
    lazy: true,
  },
  'rework': {
    component: ReworkDashboard,
    title: 'Rework QA',
    lazy: true,
  },
  'data-quality': {
    component: DataQualityDashboard,
    title: 'Data Quality',
    lazy: true,
  },
  'feedback': {
    component: FeedbackDashboard,
    title: 'Feedback',
    lazy: true,
  },
  'notifications': {
    component: NotificationAdmin,
    title: 'Notifications',
    lazy: true,
  },
  'system-health': {
    component: SystemHealthDashboard,
    title: 'System Health',
    lazy: true,
  },
  'work-instructions': {
    component: WorkInstructionsPage,
    title: 'Work Instructions',
    lazy: true,
  },
  'design-system': {
    component: DesignSystem,
    title: 'Design System',
    lazy: true,
  },

  // ---------------------------------------------------------------------------
  // Account Section (actions, not full views)
  // ---------------------------------------------------------------------------
  'profile': {
    component: null,
    title: 'Profile',
    action: 'showProfile',
  },
  'downloads': {
    component: null,
    title: 'Downloads',
    action: 'showDownloads',
  },
  'send-feedback': {
    component: null,
    title: 'Send Feedback',
    action: 'showFeedback',
  },

  // ---------------------------------------------------------------------------
  // Legacy aliases (for backwards compatibility with bookmarks/links)
  // ---------------------------------------------------------------------------
  'projects': {
    component: ProjectSchedule,
    title: 'Project Tracker',
    alias: 'project-tracker',
  },
  'team-hours': {
    component: TodaysSessions,
    title: 'Time Reports',
    alias: 'time-reports',
  },
  'pa-queue': {
    component: PAQueue,
    title: 'PA View',
    alias: 'pa-view',
  },
  'assembly': {
    component: AssemblyQueue,
    title: 'Assembly View',
    alias: 'assembly-view',
  },
  'shipping': {
    component: PlaceholderView,
    title: 'Shipping View',
    alias: 'shipping-view',
    placeholder: true,
  },
};

/**
 * Get the current view title (for breadcrumbs/header)
 * @param {string} viewId - View ID
 * @returns {string} View title
 */
export function getCurrentViewTitle(viewId) {
  return VIEW_REGISTRY[viewId]?.title || 'Unknown';
}

/**
 * Get view configuration
 * @param {string} viewId - View ID
 * @returns {object|null} View configuration
 */
export function getViewConfig(viewId) {
  return VIEW_REGISTRY[viewId] || null;
}

/**
 * Check if a view is a placeholder
 * @param {string} viewId - View ID
 * @returns {boolean} Whether view is a placeholder
 */
export function isPlaceholderView(viewId) {
  return VIEW_REGISTRY[viewId]?.placeholder === true;
}

/**
 * Check if a view uses lazy loading
 * @param {string} viewId - View ID
 * @returns {boolean} Whether view is lazy loaded
 */
export function isLazyView(viewId) {
  return VIEW_REGISTRY[viewId]?.lazy === true;
}
